import React from 'react';

/*
 * Flat, cartoon-style illustration of a car and a bike "driving" past each other,
 * built entirely from inline SVG shapes (no external assets). Wheels spin and the
 * vehicles gently bob, driven by CSS keyframes scoped to this component only.
 */
const GarageScene = () => (
  <div className="garage-scene" aria-hidden="true">
    <style>{`
      .garage-scene {
        position: relative;
        width: 100%;
        height: 260px;
        overflow: hidden;
      }
      .garage-scene .road {
        position: absolute; left: 0; right: 0; bottom: 36px; height: 4px;
        background: repeating-linear-gradient(90deg, var(--black-muted) 0 24px, transparent 24px 44px);
        opacity: 0.6;
      }
      .garage-scene .ground {
        position: absolute; left: 0; right: 0; bottom: 0; height: 40px;
        background: var(--black-border); opacity: 0.35;
      }
      .car-rig {
        position: absolute; bottom: 38px; left: -320px;
        animation: driveCar 13s linear infinite;
      }
      .bike-rig {
        position: absolute; bottom: 38px; left: -220px;
        animation: driveBike 9s linear infinite;
        animation-delay: 1.4s;
      }
      .bob { animation: bob 0.5s ease-in-out infinite alternate; }
      .wheel { animation: spin 0.6s linear infinite; transform-box: fill-box; transform-origin: center; }
      @keyframes driveCar {
        0%   { left: -340px; }
        100% { left: 110%; }
      }
      @keyframes driveBike {
        0%   { left: -220px; }
        100% { left: 110%; }
      }
      @keyframes bob {
        from { transform: translateY(0); }
        to   { transform: translateY(-4px); }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .car-rig, .bike-rig, .bob, .wheel { animation: none !important; }
      }
    `}</style>

    <div className="ground" />
    <div className="road" />

    {/* Car */}
    <div className="car-rig">
      <svg width="220" height="110" viewBox="0 0 220 110" className="bob">
        <ellipse cx="110" cy="98" rx="90" ry="8" fill="var(--black-muted)" opacity="0.25" />
        {/* Body */}
        <path d="M20 82 C16 68 24 58 40 55 L66 55 L82 26 C86 18 94 14 103 14 L140 14 C150 14 159 19 165 28 L180 55 C198 56 208 65 208 78 C208 82 205 85 201 85 L24 85 C21 85 19 84 20 82 Z"
          fill="var(--gold)" />
        {/* Cabin / windows */}
        <path d="M84 55 L98 30 C100 26 104 24 108 24 L136 24 C142 24 148 27 151 32 L163 55 Z"
          fill="var(--gold-pale)" opacity="0.92" />
        <line x1="120" y1="26" x2="120" y2="55" stroke="var(--gold-dark)" strokeWidth="3" />
        {/* Rocker panel */}
        <rect x="22" y="76" width="184" height="7" rx="3" fill="var(--gold-dark)" opacity="0.45" />
        {/* Wheels (drawn last, on top of the body silhouette) */}
        <g className="wheel">
          <circle cx="60" cy="88" r="17" fill="var(--black-rich)" />
          <circle cx="60" cy="88" r="9" fill="var(--gold-bright)" />
          <circle cx="60" cy="88" r="9" fill="none" stroke="var(--black-rich)" strokeWidth="1.5" />
        </g>
        <g className="wheel">
          <circle cx="168" cy="88" r="17" fill="var(--black-rich)" />
          <circle cx="168" cy="88" r="9" fill="var(--gold-bright)" />
          <circle cx="168" cy="88" r="9" fill="none" stroke="var(--black-rich)" strokeWidth="1.5" />
        </g>
        <circle cx="197" cy="50" r="3.5" fill="var(--gold-pale)" />
        <rect x="20" y="62" width="12" height="5" rx="2" fill="var(--danger)" />
      </svg>
    </div>

    {/* Bike */}
    <div className="bike-rig">
      <svg width="160" height="100" viewBox="0 0 160 100" className="bob">
        <ellipse cx="80" cy="90" rx="65" ry="7" fill="var(--black-muted)" opacity="0.25" />
        <circle cx="34" cy="70" r="22" fill="none" stroke="var(--text-primary)" strokeWidth="4" opacity="0.9" className="wheel" />
        <circle cx="34" cy="70" r="6" fill="var(--gold-bright)" className="wheel" />
        <circle cx="122" cy="70" r="22" fill="none" stroke="var(--text-primary)" strokeWidth="4" opacity="0.9" className="wheel" />
        <circle cx="122" cy="70" r="6" fill="var(--gold-bright)" className="wheel" />
        <path d="M34 70 L64 40 L92 40 L122 70" fill="none" stroke="var(--gold)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M64 40 L58 22" fill="none" stroke="var(--gold)" strokeWidth="5" strokeLinecap="round" />
        <path d="M92 40 L96 70" fill="none" stroke="var(--gold)" strokeWidth="5" strokeLinecap="round" />
        <path d="M50 22 L68 22" fill="none" stroke="var(--gold-dark)" strokeWidth="5" strokeLinecap="round" />
        <rect x="86" y="34" width="22" height="10" rx="4" fill="var(--gold-dark)" />
        <circle cx="58" cy="22" r="3" fill="var(--gold-pale)" />
      </svg>
    </div>
  </div>
);

export default GarageScene;
